import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

// The backend stores timestamps as Unix ms and serializes them as ISO-8601
// strings in UTC. If we use `dayjs(iso)` naively, dayjs treats the ISO as
// LOCAL time, which makes a UTC 23:00 message render as 23:00 in the
// viewer's timezone. That means a "Today" header can show up as "Yesterday"
// in another browser that is hours behind. To make date grouping deterministic
// across browsers (the day boundary must be the SAME local day for every user
// viewing the same UTC instant), we always parse via `dayjs.utc(iso)` and
// then convert to local before comparing with `dayjs()`.
function parseDate(iso: string | null | undefined): dayjs.Dayjs {
  if (!iso) return dayjs(0);
  // The API contract is "ISO-8601 in UTC". dayjs.utc() respects the trailing
  // `Z` and yields the absolute instant; calling `.local()` is a no-op for
  // arithmetic but keeps the calendar fields in the viewer's timezone so
  // `isSame(..., 'day')` compares the local day correctly.
  return dayjs.utc(iso).local();
}

export function timeShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = parseDate(iso);
  const now = dayjs();
  if (d.isSame(now, 'day')) return d.format('HH:mm');
  if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.isSame(now, 'week')) return d.format('ddd');
  if (d.year() === now.year()) return d.format('D MMM');
  return d.format('D MMM YYYY');
}

export function fromNow(iso: string | null | undefined): string {
  if (!iso) return 'a long time ago';
  return parseDate(iso).fromNow();
}

export function formatMessageStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  return parseDate(iso).format('HH:mm');
}

export function formatDateDivider(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = parseDate(iso);
  const now = dayjs();
  if (d.isSame(now, 'day')) return 'Today';
  if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.year() === now.year()) return d.format('dddd, D MMMM');
  return d.format('dddd, D MMMM YYYY');
}

export function initials(name: string | null | undefined, fallback = '?'): string {
  if (!name) return fallback;
  const trimmed = name.trim();
  if (!trimmed) return fallback;
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
